/**
 * The remaining definition lists: checklist titles, score bands, action firms, central
 * teams and the two closed-day calendars.
 */
import { request } from './client';
import type { Problem } from './problem';

export type ClosedDayScope = 'Country' | 'Location';

export interface ChecklistTitle {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  categoryIds: string[];
  categoryNames: string[];
  /** How many questions the title reaches, across all its categories. */
  questionCount: number;
}

export interface ScoreBand {
  id: string;
  code: string;
  name: string;
  from: number;
  /** Where the next band starts. Null for the top one, which runs to 100. */
  until: number | null;
  colour: string | null;
  isActive: boolean;
}

export interface ScoreBands {
  bands: ScoreBand[];
  /** Whatever the set as a whole is still missing. */
  problems: Problem[];
}

export interface ActionCompany {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  addresses: string[];
}

export interface CentralTeam {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

export interface ClosedDay {
  id: string;
  scope: ClosedDayScope;
  scopeId: string;
  scopeName: string;
  /** ISO date, no time. */
  date: string;
  name: string;
}

export type SaveChecklistTitle = {
  name: string;
  description: string | null;
  isActive: boolean;
  categoryIds: string[];
};

export type SaveScoreBand = Pick<ScoreBand, 'code' | 'name' | 'from' | 'colour' | 'isActive'>;
export type SaveActionCompany = Pick<
  ActionCompany,
  'name' | 'description' | 'isActive' | 'addresses'
>;
export type SaveCentralTeam = Pick<CentralTeam, 'name' | 'description' | 'isActive'>;
export type SaveClosedDay = Pick<ClosedDay, 'scope' | 'scopeId' | 'date' | 'name'>;

const base = '/api/v1/parameters';
const calendar = '/api/v1/organisation/closed-days';
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

const query = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const suffix = search.toString();
  return suffix ? `?${suffix}` : '';
};

function crud<TRow, TSave>(path: string) {
  return {
    list: () => request<TRow[]>(`${base}/${path}`),
    create: (body: TSave) => request<TRow>(`${base}/${path}`, json('POST', body)),
    update: (id: string, body: TSave) => request<TRow>(`${base}/${path}/${id}`, json('PUT', body)),
    remove: (id: string) => request<void>(`${base}/${path}/${id}`, { method: 'DELETE' }),
  };
}

export const parameters = {
  checklistTitles: crud<ChecklistTitle, SaveChecklistTitle>('checklist-titles'),
  actionCompanies: crud<ActionCompany, SaveActionCompany>('action-companies'),
  centralTeams: crud<CentralTeam, SaveCentralTeam>('central-teams'),

  // The band endpoints answer with the whole set, because adding one changes where the one
  // above it ends and a screen that patched a single row would show a stale range.
  scoreBands: {
    list: () => request<ScoreBands>(`${base}/score-bands`),
    create: (body: SaveScoreBand) => request<ScoreBands>(`${base}/score-bands`, json('POST', body)),
    update: (id: string, body: SaveScoreBand) =>
      request<ScoreBands>(`${base}/score-bands/${id}`, json('PUT', body)),
    remove: (id: string) => request<ScoreBands>(`${base}/score-bands/${id}`, { method: 'DELETE' }),
  },

  closedDays: {
    list: (params: Record<string, string | undefined> = {}) =>
      request<ClosedDay[]>(`${calendar}${query(params)}`),
    create: (body: SaveClosedDay) => request<ClosedDay>(calendar, json('POST', body)),
    update: (id: string, body: SaveClosedDay) =>
      request<ClosedDay>(`${calendar}/${id}`, json('PUT', body)),
    remove: (id: string) => request<void>(`${calendar}/${id}`, { method: 'DELETE' }),
  },
};
